import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgEye from '@icons/eye.svg?react';
import type { TableView } from './types';

interface ComponentProps {
  visible: boolean;
  views: TableView[];
  /** Shows the tab again and opens it; the sidebar closes, since the tab is then in front of the user. */
  onUnhide: (viewId: string) => void;
  onClose: () => void;
}

/** "Skryté pohledy" (no Figma design yet): the colleagues' tabs the user hid, each with a way back. */
const HiddenViewsSidebar: React.FC<ComponentProps> = ({ visible, views, onUnhide, onClose }) => {
  const { tr } = useTranslationLgs();
  return (
    <RingilSidebar visible={visible} heading={tr('TableViews.hiddenViews', 'Hidden views')} onContinue={onClose} onBack={onClose} showSaveButton={false} showBackButton>
      <div className="flex flex-col gap-2 font-['DM_Sans']" data-testid="table-view-hidden-list">
        <p className="m-0 text-xs text-content-secondary">
          {tr('TableViews.hiddenViewsHint', 'Views your colleagues shared and you hid. Show one to get its tab back.')}
        </p>
        {views.map(view => (
          <div key={view.id} className="flex items-center gap-2.5 rounded bg-base-secondary px-3 py-2" data-testid={`table-view-hidden-${view.id}`}>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-content-primary">{view.name}</span>
              {view.ownerFullName && (
                <span className="truncate text-xs text-content-secondary">{tr('TableViews.sharedBy', 'Shared by {{name}}', { name: view.ownerFullName })}</span>
              )}
            </span>
            <RingilButton
              mode="link"
              label={tr('TableViews.showView', 'Show')}
              // The SVG paints its paths black; take the button's colour instead, hover included.
              icon={<SvgEye className="w-4 h-4 [&_path]:fill-current" />}
              onClick={() => {
                onUnhide(view.id);
                onClose();
              }}
              dataTestId={`table-view-unhide-${view.id}`}
            />
          </div>
        ))}
      </div>
    </RingilSidebar>
  );
};

export default HiddenViewsSidebar;
